import React, { useState, useEffect } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { PERMISSION_SCHEMA, ROLE_PRESETS } from '../permissions';
import { UserPlus, Trash2, KeyRound, Shield, Eye, Edit3, X, Check } from 'lucide-react';

const ROLE_DEFAULTS = {
  admin: null,
  supervisor: {
    live_map: { view: true }, invoices: { view: true, upload: false, delete: false },
    route_planner: { view: true, optimize: false, assign: false },
    drivers: { view: true, edit: false, delete: false },
    driver_reports: { view: true, delete: false }, journey_history: { view: true },
    analytics: { view: true }, rescheduled: { view: true, assign: false },
    settings: { view: false }, users: { view: false },
  },
  viewer: {
    live_map: { view: true }, invoices: { view: false }, route_planner: { view: false },
    drivers: { view: false }, driver_reports: { view: true }, journey_history: { view: true },
    analytics: { view: true }, rescheduled: { view: false }, settings: { view: false }, users: { view: false },
  },
};

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'viewer', permissions: {} });
  const [pwModal, setPwModal] = useState(null);
  const [newPw, setNewPw] = useState('');

  const load = () => api.get('/users').then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => { setUsers([]); toast.error('Failed to load users'); });
  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setEditUser(null);
    setForm({ name: '', email: '', password: '', role: 'viewer', permissions: JSON.parse(JSON.stringify(ROLE_DEFAULTS.viewer)) });
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({
      name: u.name, email: u.email, password: '', role: u.role,
      permissions: u.role === 'admin' ? {} : (u.permissions || JSON.parse(JSON.stringify(ROLE_DEFAULTS[u.role] || ROLE_DEFAULTS.viewer)))
    });
    setShowModal(true);
  };

  const onRoleChange = (role) => {
    setForm(f => ({ ...f, role, permissions: role === 'admin' ? {} : JSON.parse(JSON.stringify(ROLE_DEFAULTS[role] || ROLE_DEFAULTS.viewer)) }));
  };

  const togglePerm = (page, action) => {
    setForm(f => {
      const perms = JSON.parse(JSON.stringify(f.permissions));
      if (!perms[page]) perms[page] = {};
      perms[page][action] = !perms[page][action];
      // If turning off view, turn off all other actions for that page
      if (action === 'view' && !perms[page][action]) {
        Object.keys(perms[page]).forEach(a => perms[page][a] = false);
      }
      // If turning on any action, ensure view is on
      if (action !== 'view' && perms[page][action]) perms[page].view = true;
      return { ...f, permissions: perms };
    });
  };

  const save = async () => {
    if (!form.name || !form.email) return toast.error('Name and email required');
    if (!editUser && (!form.password || form.password.length < 6)) return toast.error('Password min 6 characters');
    try {
      if (editUser) {
        await api.put('/users/' + editUser.id, { name: form.name, role: form.role, permissions: form.role === 'admin' ? null : form.permissions });
        toast.success('User updated');
      } else {
        await api.post('/users', { name: form.name, email: form.email, password: form.password, role: form.role, permissions: form.role === 'admin' ? null : form.permissions });
        toast.success('User created');
      }
      setShowModal(false);
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Save failed'); }
  };

  const toggleActive = async (u) => {
    try {
      await api.put('/users/' + u.id, { is_active: !u.is_active });
      toast.success(u.is_active ? 'User disabled' : 'User enabled');
      load();
    } catch { toast.error('Failed'); }
  };

  const resetPw = async () => {
    if (!newPw || newPw.length < 6) return toast.error('Password min 6 characters');
    try {
      await api.post('/users/' + pwModal.id + '/password', { password: newPw });
      toast.success('Password reset');
      setPwModal(null); setNewPw('');
    } catch { toast.error('Failed'); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete('/users/' + u.id);
      toast.success('User deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const roleBadge = (role) => {
    const map = { admin: ['#7c3aed', '#f3e8ff'], supervisor: ['#0284c7', '#e0f2fe'], viewer: ['#64748b', '#f1f5f9'] };
    const [c, bg] = map[role] || map.viewer;
    return <span style={{ padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, color: c, background: bg, textTransform: 'capitalize' }}>{role}</span>;
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>User Management</h1>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Add supervisors and viewers with custom permissions</p>
        </div>
        <button onClick={openAdd} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>
          <UserPlus size={16} /> Add User
        </button>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        {(Array.isArray(users) ? users : []).map(u => (
          <div key={u.id} style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderBottom: '1px solid #f1f5f9', gap: 12, opacity: u.is_active === false ? 0.5 : 1 }}>
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Shield size={18} color="#64748b" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: '#1e293b', fontSize: 14 }}>{u.name} {u.id === 1 && <span style={{ fontSize: 10, color: '#7c3aed' }}>★ Master</span>}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{u.email}</div>
            </div>
            {roleBadge(u.role)}
            {u.is_active === false && <span style={{ fontSize: 11, color: '#dc2626', fontWeight: 600 }}>Disabled</span>}
            <div style={{ display: 'flex', gap: 6 }}>
              {u.id !== 1 && (
                <>
                  <button onClick={() => openEdit(u)} title="Edit" style={iconBtn}><Edit3 size={15} /></button>
                  <button onClick={() => setPwModal(u)} title="Reset password" style={iconBtn}><KeyRound size={15} /></button>
                  <button onClick={() => toggleActive(u)} title={u.is_active === false ? 'Enable' : 'Disable'} style={iconBtn}>{u.is_active === false ? <Check size={15} /> : <X size={15} />}</button>
                  <button onClick={() => del(u)} title="Delete" style={{ ...iconBtn, color: '#dc2626' }}><Trash2 size={15} /></button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit modal */}
      {showModal && (
        <div style={overlay}>
          <div style={{ ...modal, maxWidth: 640 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>{editUser ? 'Edit User' : 'Add User'}</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
              <div>
                <label style={lbl}>Name</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inp} />
              </div>
              <div>
                <label style={lbl}>Email</label>
                <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} disabled={!!editUser} style={{ ...inp, opacity: editUser ? 0.6 : 1 }} />
              </div>
            </div>

            {!editUser && (
              <div style={{ marginBottom: 14 }}>
                <label style={lbl}>Password</label>
                <input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} style={inp} placeholder="Min 6 characters" />
              </div>
            )}

            <div style={{ marginBottom: 16 }}>
              <label style={lbl}>Role</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {['admin', 'supervisor', 'viewer'].map(r => (
                  <button key={r} onClick={() => onRoleChange(r)} style={{
                    flex: 1, padding: '10px', borderRadius: 8, border: form.role === r ? '2px solid #0284c7' : '1px solid #e2e8f0',
                    background: form.role === r ? '#eff6ff' : '#fff', cursor: 'pointer', textAlign: 'left'
                  }}>
                    <div style={{ fontWeight: 700, fontSize: 13, textTransform: 'capitalize', color: '#1e293b' }}>{r}</div>
                    <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{ROLE_PRESETS[r]}</div>
                  </button>
                ))}
              </div>
            </div>

            {form.role !== 'admin' && (
              <div style={{ marginBottom: 16 }}>
                <label style={lbl}>Permissions</label>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                  {PERMISSION_SCHEMA.map(({ page, label, actions }) => (
                    <div key={page} style={{ display: 'flex', alignItems: 'center', padding: '9px 12px', borderBottom: '1px solid #f1f5f9', gap: 10 }}>
                      <div style={{ flex: 1, fontSize: 13, color: '#1e293b', fontWeight: 500 }}>{label}</div>
                      <div style={{ display: 'flex', gap: 12 }}>
                        {actions.map(action => (
                          <label key={action} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: '#64748b', cursor: 'pointer', textTransform: 'capitalize' }}>
                            <input type="checkbox" checked={!!(form.permissions[page] && form.permissions[page][action])} onChange={() => togglePerm(page, action)} />
                            {action}
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Turning off "view" disables all other actions for that page.</p>
              </div>
            )}

            {form.role === 'admin' && (
              <div style={{ background: '#f3e8ff', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 12, color: '#7c3aed' }}>
                <Shield size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: 'middle' }} />
                Admins have full access to everything including user management.
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 18px', background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={save} style={{ padding: '10px 18px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>{editUser ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Password reset modal */}
      {pwModal && (
        <div style={overlay}>
          <div style={modal}>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Reset Password</h2>
            <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>New password for {pwModal.name}</p>
            <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} style={inp} placeholder="Min 6 characters" />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => { setPwModal(null); setNewPw(''); }} style={{ padding: '10px 18px', background: '#f1f5f9', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={resetPw} style={{ padding: '10px 18px', background: '#0284c7', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>Reset</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const iconBtn = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: 7, cursor: 'pointer', color: '#64748b', display: 'flex', alignItems: 'center' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 };
const modal = { background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420, maxHeight: '90vh', overflowY: 'auto' };
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 6 };
const inp = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };
